import React, { useCallback, useState, useRef, useEffect } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  useDisclosure,
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { useLocation } from 'react-router-dom';

import Header from 'innovate/components/common/Header';
import { AdminSidebar } from './components/AdminSidebar';
import 'innovate/pages/AdminPages/Scenarios.css';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const {
    isOpen: isMobileMenuOpen,
    onOpen: onMobileMenuOpen,
    onClose: onMobileMenuClose,
  } = useDisclosure();

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
  }, []);

  // Scroll to top when route changes or component mounts
  useEffect(() => {
    // Scroll window to top
    window.scrollTo(0, 0);

    // Scroll content container to top
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  return (
    <Box minH="100vh" bg="#F4F4F5" mb={2}>
      <Header />

      <Flex
        align="stretch"
        gap={{ base: 0, lg: 5 }}
        px={{ base: 0, lg: 5 }}
        pt={{ base: 5, lg: 4 }}
        pb={{ base: 5, lg: 4 }}
        minH="calc(100vh - 70px)"
      >
        {/* Desktop Sidebar */}
        <Box
          position="fixed"
          left="20px"
          top="80px"
          bottom="26px"
          display={{ base: 'none', lg: 'block' }}
          zIndex={5}
        >
          <AdminSidebar isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} />
        </Box>

        {/* Mobile Sidebar */}
        <Drawer isOpen={isMobileMenuOpen} placement="left" onClose={onMobileMenuClose}>
          <DrawerOverlay />

          <DrawerContent maxW="260px" bg="transparent" boxShadow="none">
            <AdminSidebar isCollapsed={false} onToggle={onMobileMenuClose} />
          </DrawerContent>
        </Drawer>

        {/* Spacer for fixed sidebar on desktop */}
        <Box
          display={{ base: 'none', lg: 'block' }}
          flexShrink={0}
          w={isSidebarCollapsed ? '60px' : '200px'}
          transition="width 0.3s ease"
        />

        {/* Main Content */}
        <Box flex="1" minW={0} position="relative" mt={1}>
          <IconButton
            display={{ base: 'flex', lg: 'none' }}
            icon={<HamburgerIcon />}
            aria-label="Open admin menu"
            variant="ghost"
            onClick={onMobileMenuOpen}
            position="absolute"
            top={3}
            left={3}
            zIndex={10}
          />

          <Box
            ref={contentRef}
            bg="white"
            borderRadius={{ base: 0, lg: '22px' }}
            minH="calc(100vh - 70px)"
            px={{ base: 4, md: 5, xl: 6 }}
            pt={{ base: 16, lg: 4 }}
            pb={6}
          >
            {children}
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};
